import type { ForgeUIContentBylineItemExtensionType } from '@confluence/forge-ui/entry-points/types.ts';
import { getBylineAppContentPropertyKeys } from '@confluence/forge-ui/entry-points/bylineContentPropertyKeys.ts';
import { extensionTitle } from '@confluence/forge-ui/entry-points/extensionTitle.ts';
import type { useWebItemLocationWebItemType as WebItem } from '@confluence/web-item-location/entry-points/useWebItemLocation.ts';

import {
	type ContentPropertyConnection,
	validateContentPropertyData,
} from './validateContentPropertyData.tsx';

export type ByLineAppProducer = 'connect' | 'forge';
export type ByLineAppProducerStatus = 'complete' | 'failed' | 'pending';
export type ByLineAppSnapshotCompleteness = 'complete' | 'failed' | 'partial';
export type ByLineAppPresentationSource =
	| 'connectWebItem'
	| 'contentProperty'
	| 'dynamicProperties'
	| 'manifest';

export type ByLineAppSnapshotItem = {
	id: string;
	order: number;
	producer: ByLineAppProducer;
	presentation: {
		icon?: {
			height: number;
			src: string;
			width: number;
		};
		label: string;
		resolution: 'resolved' | 'unresolved';
		source: ByLineAppPresentationSource;
		tooltip?: string;
	};
};

export type ByLineAppSnapshot = {
	apps: ByLineAppSnapshotItem[];
	completeness: ByLineAppSnapshotCompleteness;
	producers: {
		connect: ByLineAppProducerStatus;
		forge: ByLineAppProducerStatus;
	};
};

export const isByLineAppSnapshotAuthoritative = (snapshot: ByLineAppSnapshot): boolean =>
	snapshot.completeness === 'complete' &&
	snapshot.producers.connect === 'complete' &&
	snapshot.producers.forge === 'complete';

type CreateByLineAppSnapshotOptions = {
	connectError?: unknown;
	connectItems: WebItem[];
	forgeError?: unknown;
	forgeItems: ReadonlyArray<ForgeUIContentBylineItemExtensionType>;
	loadingConnectItems: boolean;
	loadingForgeItems: boolean;
	bylineAppContentProperties?: ContentPropertyConnection | null;
};

const getProducerStatus = ({
	error,
	hasItems,
	loading,
}: {
	error?: unknown;
	hasItems: boolean;
	loading: boolean;
}): ByLineAppProducerStatus => {
	if (error) {
		return 'failed';
	}

	if (loading && !hasItems) {
		return 'pending';
	}

	return 'complete';
};

const getCompleteness = (
	connect: ByLineAppProducerStatus,
	forge: ByLineAppProducerStatus,
): ByLineAppSnapshotCompleteness => {
	if (connect === 'complete' && forge === 'complete') {
		return 'complete';
	}

	if (connect === 'failed' && forge === 'failed') {
		return 'failed';
	}

	return 'partial';
};

// Only called after the SSR byline experiment guard in SingleLineByLine.
export const createByLineAppSnapshot = ({
	connectError,
	connectItems,
	forgeError,
	forgeItems,
	loadingConnectItems,
	loadingForgeItems,
	bylineAppContentProperties,
}: CreateByLineAppSnapshotOptions): ByLineAppSnapshot => {
	const connectStatus = getProducerStatus({
		error: connectError,
		hasItems: connectItems.length > 0,
		loading: loadingConnectItems,
	});
	const forgeStatus = getProducerStatus({
		error: forgeError,
		hasItems: forgeItems.length > 0,
		loading: loadingForgeItems,
	});

	const connectApps: ByLineAppSnapshotItem[] = connectItems.map((item, order) => ({
		id: item.completeKey || item.id || `connect-${order}`,
		order,
		producer: 'connect',
		presentation: {
			...(item.icon?.path
				? {
						icon: {
							height: item.icon.height ?? 14,
							src: item.icon.path,
							width: item.icon.width ?? 14,
						},
					}
				: {}),
			label: item.label ?? '',
			resolution: 'resolved',
			source: 'connectWebItem',
			...(item.tooltip ? { tooltip: item.tooltip } : {}),
		},
	}));

	const forgeApps: ByLineAppSnapshotItem[] = forgeItems.map((app, index) => {
		const contentPropertyKey = app.properties.contentPropertyKey;
		const hasDynamicProperties = Boolean(app.properties.dynamicProperties);
		const defaultPresentation = {
			contentPropertyKey: contentPropertyKey ?? '',
			icon: app.properties.icon ?? '',
			title: app.properties.title ?? '',
			tooltip: app.properties.tooltip ?? '',
		};
		const contentPropertyPresentation =
			contentPropertyKey && bylineAppContentProperties?.nodes?.length
				? validateContentPropertyData(
						{ content: { nodes: [{ properties: bylineAppContentProperties }] } },
						defaultPresentation,
						getBylineAppContentPropertyKeys([app]),
					)
				: defaultPresentation;
		const presentationIcon = contentPropertyPresentation?.icon || app.properties.icon;
		const presentationTitle = contentPropertyPresentation?.title || app.properties.title;
		const presentationTooltip = contentPropertyPresentation?.tooltip || app.properties.tooltip;
		// Validation returns the original defaults when stored data cannot be used.
		const source: ByLineAppPresentationSource = contentPropertyKey
			? contentPropertyPresentation !== defaultPresentation
				? 'contentProperty'
				: 'manifest'
			: hasDynamicProperties
				? 'dynamicProperties'
				: 'manifest';

		return {
			id: app.id,
			order: connectApps.length + index,
			producer: 'forge',
			presentation: {
				...(presentationIcon
					? {
							icon: {
								height: 14,
								src: presentationIcon,
								width: 14,
							},
						}
					: {}),
				label: presentationTitle ? extensionTitle(app, presentationTitle) : '',
				resolution: presentationTitle ? 'resolved' : 'unresolved',
				source,
				...(presentationTooltip ? { tooltip: presentationTooltip } : {}),
			},
		};
	});

	return {
		apps: [...connectApps, ...forgeApps],
		completeness: getCompleteness(connectStatus, forgeStatus),
		producers: {
			connect: connectStatus,
			forge: forgeStatus,
		},
	};
};
